import type { Document, DocumentVersion } from "@models";
import type { JSONContent } from "@tiptap/core";
import type { Editor } from "@tiptap/react";
import { useCallback, useState } from "react";
import { useSnackbar } from "src/components/Snackbar";
import { getDynamicContent } from "src/services";
import type { DynamicContentResolution } from "src/tiptap/components/tiptap-node/dynamic-nodes";
import { buildDynamicNodeResolver } from "src/tiptap/components/tiptap-node/dynamic-nodes/resolution";
import { buildDownloadFileName, captureSentryException } from "src/utils";
import type { TiptapTopBarExportAction } from "./TiptapDocViewerTopBar";
import {
  collectDataKeysFromTiptapExportDocument,
  type DynamicNodeExportResolver,
} from "./tiptapDynamicExportNodes";
import {
  downloadTiptapDocx,
  downloadTiptapMarkdown,
  downloadTiptapPdf,
  getTiptapExportFileName,
  type TiptapExportFormat,
} from "./tiptapExport";

export type TiptapExportActions = {
  docx: TiptapTopBarExportAction | null;
  markdown: TiptapTopBarExportAction | null;
  pdf: TiptapTopBarExportAction | null;
};

const getTiptapExportBaseFileName = ({
  deviceName,
  document,
  organizationName,
}: Pick<
  TiptapExportContext,
  "deviceName" | "document" | "organizationName"
>) =>
  buildDownloadFileName({
    baseName: document.title || document.templateKey || "document",
    deviceName,
    organizationName,
  });

type TiptapExportContext = {
  deviceName?: string | null;
  document: Document;
  editor: Editor;
  orgId: string;
  organizationName: string;
  selectedDocVersion: DocumentVersion;
};

type TiptapExportActionsConfig = TiptapExportContext & {
  disabled: boolean;
};

type TiptapExportResolverContext = Pick<
  TiptapExportContext,
  "document" | "orgId" | "selectedDocVersion"
> & {
  content: JSONContent;
};

const getExportResolver = async ({
  content,
  document,
  orgId,
  selectedDocVersion,
}: TiptapExportResolverContext): Promise<DynamicNodeExportResolver> => {
  const { data } = await getDynamicContent(orgId, document.id, {
    versionId: selectedDocVersion.id,
    dataKeys: collectDataKeysFromTiptapExportDocument(content),
  });
  const dynamicContent =
    data && typeof data === "object"
      ? (data as DynamicContentResolution)
      : { dataKeys: {}, context: {} };

  return buildDynamicNodeResolver({
    dataKeys: dynamicContent.dataKeys ?? {},
    context: dynamicContent.context ?? {},
  });
};

const exportFileExtensions: Record<TiptapExportFormat, "docx" | "md" | "pdf"> =
  {
    docx: "docx",
    markdown: "md",
    pdf: "pdf",
  };

const exportFormatLabels: Record<TiptapExportFormat, string> = {
  docx: "DOCX",
  markdown: "Markdown",
  pdf: "PDF",
};

export const useTiptapExportActions = ({
  deviceName,
  disabled,
  document,
  editor,
  orgId,
  organizationName,
  selectedDocVersion,
}: TiptapExportActionsConfig): TiptapExportActions => {
  const { showSnackbar } = useSnackbar();
  const [exportingFormat, setExportingFormat] =
    useState<TiptapExportFormat | null>(null);
  const exportOrganizationName = organizationName.trim();
  const isExportDisabled = disabled || exportingFormat !== null;

  const handleDownload = useCallback(
    async ({
      deviceName,
      document,
      editor,
      format,
      organizationName,
      orgId,
      selectedDocVersion,
    }: TiptapExportContext & { format: TiptapExportFormat }) => {
      setExportingFormat(format);

      try {
        const content = editor.getJSON();
        const exportMetadataContext = {
          document,
          organizationName,
          selectedDocVersion,
        };
        const commonParams = {
          document: content,
          fileName: getTiptapExportFileName(
            getTiptapExportBaseFileName({
              deviceName,
              document,
              organizationName,
            }),
            exportFileExtensions[format]
          ),
          exportMetadataContext,
          resolver: await getExportResolver({
            content,
            document,
            orgId,
            selectedDocVersion,
          }),
        };

        if (format === "docx") {
          await downloadTiptapDocx(commonParams);
        } else if (format === "pdf") {
          await downloadTiptapPdf({ ...commonParams, orgId });
        } else {
          await downloadTiptapMarkdown({ ...commonParams, editor });
        }
      } catch (error) {
        captureSentryException(error, {
          action: `tiptap-${format}-export`,
          documentId: document.id,
        });
        showSnackbar(
          `Could not export ${exportFormatLabels[format]}. Please try again.`
        );
      } finally {
        setExportingFormat(null);
      }
    },
    [showSnackbar]
  );

  if (!exportOrganizationName) {
    return { docx: null, markdown: null, pdf: null };
  }

  const exportContext = {
    deviceName,
    document,
    editor,
    organizationName: exportOrganizationName,
    orgId,
    selectedDocVersion,
  };
  const actionFor = (format: TiptapExportFormat): TiptapTopBarExportAction => ({
    isDisabled: isExportDisabled,
    onClick: () => {
      void handleDownload({ ...exportContext, format });
    },
  });

  return {
    docx: actionFor("docx"),
    markdown: actionFor("markdown"),
    pdf: actionFor("pdf"),
  };
};
